import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import { authFetch } from '../services/authFetch';

const AdminSuratIjinPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: false
  });
  const [adminUsers, setAdminUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Real data for interruption permits
  const [suratIjinRequests, setSuratIjinRequests] = useState([]);
  // Always attempt to fetch data - the API will handle auth checks
  // This prevents issues where the user object might not be loaded yet

  const fetchPermits = async (date = '', status = '') => {
    // Debug user state
  
    try {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      
      // Build query parameters
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (date) params.append('date', date);
      if (status) params.append('status', status);
      params.append('page', currentPage);
      params.append('per_page', itemsPerPage);
      
      const queryString = params.toString();
      const url = `${apiBase}/api/admin/interuption-permits${queryString ? `?${queryString}` : ''}`;
      
      // Use the imported authFetch instead of window.authFetch
      const res = await authFetch(url, {
        headers: { 'Accept': 'application/json' }
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch permits: ${res.status} ${errorText}`);
      }
      
      const data = await res.json();
      
      // Handle both Laravel Resource Collection format and direct array
      const sourceData = data.data || data || [];
      
      // Map backend data to UI format
      const mapped = sourceData.map(item => {
        return {
          id: item.id,
          // Store both formatted date for display and ISO date for form inputs
          tanggalPengajuan: item.created_at ? new Date(item.created_at).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }) : '',
          // Keep original date in ISO format for date inputs
          tanggalPengajuanISO: item.created_at ? item.created_at.slice(0,10) : '',
          // Student name field is correct
          siswaMengajukan: item.student ? item.student.name : '',
          student: item.student,
          // Student has 'class' field, not 'kelas'
          kelas: item.student ? (item.student.class || '') : '',
          nis: item.student ? (item.student.nis || '') : '',
          diajukanOleh: item.created_by_teacher_id ? (item.created_by_teacher ? item.created_by_teacher.name : 'Guru') : 
                       item.created_by_admin_id ? (item.created_by_admin ? item.created_by_admin.name : 'Admin') : 'Siswa',
          sedangApa: item.current_activity || '',
          pergiJam: item.leave_hour || '',
          kembali: item.return_hour || '',
          keperluan: item.purpose || '',
          tempatTujuan: item.destination || '',
          penanggungjawab: item.pic || '',
          hubungan: item.relationship || '',
          nomorTelepon: item.pic_contact_number || '',
          isSchoolActivity: item.is_school_activity || false,
          is_returning: item.is_returning ?? false,
          jam_lembur: item.jam_lembur || '',
          // Use request_status field instead of status
          status: item.request_status || item.status || 'menunggu',
          decision_by: item.decision_by,
          decision_by_id: item.decision_by_id,
        };
      });
      setSuratIjinRequests(mapped);
    } catch (e) {
      setNotification({
        show: true,
        message: `Error fetching data: ${e.message}`,
        type: 'error'
      });
      setSuratIjinRequests([]);
    }
  };

  useEffect(() => {
    fetchPermits(dateFilter, statusFilter);
    
    // Fetch admin users for decision maker info
    async function fetchAdminUsers() {
      try {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
        const res = await authFetch(`${apiBase}/api/admin_users`, {
          headers: { 'Accept': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setAdminUsers(data.data || data);
        }
      } catch (error) {
        console.error('Error fetching admin users:', error);
        setAdminUsers([]);
      }
    }
    fetchAdminUsers();
    
    // eslint-disable-next-line
  }, [user, currentPage, searchQuery, dateFilter, statusFilter]);

  const handleStudentClick = (student) => {
    if (student) {
      navigate('/admin/status-siswa', { state: { student } });
    }
  };

  const filteredRequests = suratIjinRequests.filter(request => {
    const q = searchQuery.toLowerCase();
    return (
      (request.siswaMengajukan && request.siswaMengajukan.toLowerCase().includes(q)) ||
      (request.kelas && request.kelas.toLowerCase().includes(q)) ||
      (request.nis && String(request.nis).toLowerCase().includes(q)) ||
      (request.penanggungjawab && request.penanggungjawab.toLowerCase().includes(q)) ||
      (request.keperluan && request.keperluan.toLowerCase().includes(q)) ||
      (request.tempatTujuan && request.tempatTujuan.toLowerCase().includes(q))
    );
  });

  // Pagination logic
  const totalItems = filteredRequests.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageItems = filteredRequests.slice(startIndex, endIndex);

  // Reset current page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Fetch user data from localStorage for sidebar
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Helper function to get admin name by decision_by_id
  const getDecisionMakerName = (permit) => {
    if (!permit) {
      return 'Admin';
    }

    // Ideal case: The backend sends the related decision_by object with the name.
    if (permit.decision_by && permit.decision_by.name) {
      return permit.decision_by.name;
    }

    // Fallback: If only decision_by_id is available, look up the name in adminUsers list.
    if (permit.decision_by_id && adminUsers.length > 0) {
      // Use '==' for loose comparison to avoid issues with string vs. number IDs.
      const admin = adminUsers.find(admin => admin.id == permit.decision_by_id);
      return admin ? admin.name : 'Admin';
    }
    
    return 'Admin';
  };

  // Normalize avatar URL
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <PageContainer>
      
      <PageHeader>
        <HeaderContent>
          <div>
            <h1>Surat Ijin Interupsi</h1>
            <Subtitle>Kelola dan tinjau permintaan surat ijin interupsi</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      <DashboardGrid>
        <SideSection>
          <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} />
        </SideSection>
        <MainSection>
          <ContentSection>
            <ToolbarSection>
              <FiltersContainer>
                <SearchContainer>
                  <SearchIcon>🔍</SearchIcon>
                  <SearchInput
                    type="text"
                    placeholder="Cari berdasarkan nama guru, siswa, atau mata pelajaran..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </SearchContainer>
                <FilterGroup>
                  <FilterInput
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    placeholder="Filter tanggal"
                  />
                  <FilterSelect
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">Semua Status</option>
                    <option value="pending">Menunggu</option>
                    <option value="approved">Disetujui</option>
                    <option value="rejected">Ditolak</option>
                  </FilterSelect>
                </FilterGroup>
              </FiltersContainer>
            </ToolbarSection>

            <TableContainer>
              <Table>
                <TableHeader>
                  <tr>
                    <th>Tanggal Pengajuan</th>
                    <th>Diajukan oleh</th>
                    <th>Siswa</th>
                    <th>Kelas</th>
                    <th>Sedang Apa</th>
                    <th>Pergi Jam</th>
                    <th>Kembali</th>
                    <th>Keperluan</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {filteredRequests.length > 0 ? (
                    currentPageItems.map((request) => (
                      <TableRow
                        key={request.id}
                        onClick={() => setSelectedPermit(request)}
                        selected={selectedPermit?.id === request.id}
                      >
                        <td>{request.tanggalPengajuan}</td>
                        <td>{request.diajukanOleh}</td>
                        <td>
                          <StudentLink onClick={(e) => {
                            e.stopPropagation();
                            handleStudentClick(request.student);
                          }}>
                            {request.siswaMengajukan}
                          </StudentLink>
                        </td>
                        <td>{request.kelas}</td>

                        <td>{request.sedangApa}</td>
                        <td>{request.pergiJam}</td>
                        <td>{request.kembali}</td>
                        <td>{request.keperluan}</td>

                        <td>
                          <StatusBadge status={request.status}>
                            {request.status === 'pending' || request.status === 'menunggu' ? 'Menunggu' :
                              request.status === 'approved' || request.status === 'disetujui' ? 'Disetujui' : 'Ditolak'}
                          </StatusBadge>
                        </td>
                        <td>
                          <ActionButtons>
                            <ActionButton onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPermit(request);
                            }} title="Lihat Detail">
                              👁️
                            </ActionButton>
                          </ActionButtons>
                        </td>
                      </TableRow>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', padding: '1rem' }}>
                        Tidak ada permohonan ijin
                      </td>
                    </tr>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            <PaginationContainer>
                <PaginationInfo>
                  Menampilkan {startIndex + 1}-{Math.min(endIndex, totalItems)} dari {totalItems} data
                </PaginationInfo>
                <PaginationControls>
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Sebelumnya
                  </PaginationButton>
                  
                  <PageNumbers>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <PageNumber
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          active={currentPage === pageNum}
                        >
                          {pageNum}
                        </PageNumber>
                      );
                    })}
                  </PageNumbers>
                  
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Selanjutnya ›
                  </PaginationButton>
                </PaginationControls>
              </PaginationContainer>
          </ContentSection>
        </MainSection>
      </DashboardGrid>

      {selectedPermit && (
        <DetailModal onClick={() => setSelectedPermit(null)}>
          <DetailCard onClick={e => e.stopPropagation()}>
            <SuratIjinEditModal
              selectedPermit={selectedPermit}
              setSelectedPermit={setSelectedPermit}
              adminUsers={adminUsers}
              onRefresh={() => fetchPermits(dateFilter, statusFilter)}
            />
          </DetailCard>
        </DetailModal>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast type={notification.type}>
          {notification.message}
        </NotificationToast>
      )}
    </PageContainer>
  );
};

// Reuse the same styled components from AdminKonselingPage
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const ToolbarSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  // max-width: 500px;
  background-color: var(--background-light);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  flex:1;
`;

const SearchIcon = styled.span`
  margin-right: 0.5rem;
  color: var(--text-light);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 0.875rem;
  color: var(--text-dark);
  outline: none;

  &::placeholder {
    color: var(--text-light);
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const FilterInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 0.875rem;
  color: var(--text-dark);
  background-color: var(--background);
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 0.875rem;
  color: var(--text-dark);
  background-color: var(--background);
  min-width: 150px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: var(--background-light);
  
  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-dark);
    font-size: 0.875rem;
    border-bottom: 1px solid var(--border);
  }
`;

const TableBody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  background-color: ${props => props.selected ? 'var(--background-light)' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }

  td {
    padding: 1rem;
    font-size: 0.875rem;
    color: var(--text-dark);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#fff3e0';
      case 'approved':
      case 'disetujui': return '#e6f8f5';
      case 'rejected':
      case 'ditolak': return '#ffe6e6';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#e67700';
      case 'approved':
      case 'disetujui': return '#2ec27e';
      case 'rejected':
      case 'ditolak': return '#c01c28';
      default: return 'var(--text-dark)';
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }
`;

const DetailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1000;
`;

const DetailCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const DetailHeaderContent = styled.div`
  flex: 1;
  margin-right: 1rem;
`;

const DetailStatus = styled(StatusBadge)`
  font-size: 0.875rem;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DecisionMaker = styled.div`
  font-size: 0.75rem;
  color: var(--text-light);
  font-style: italic;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: var(--text-dark);
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  grid-column: ${props => props.full ? '1 / -1' : 'auto'};
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
`;

const DetailValue = styled.div`
  font-size: 0.875rem;
  color: var(--text-dark);
  padding: 0.5rem;
  background-color: var(--background-light);
  border-radius: 8px;
  min-height: 38px; // Match input height
  display: flex;
  align-items: center;
`;

const ApproveButton = styled.button`
  flex: 1;
  background-color: var(--success, #2ec27e);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.938rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;
  
  &:hover {
    background-color: #26a96b;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RejectButton = styled(ApproveButton)`
  background-color: var(--danger, #c01c28);
  
  &:hover {
    background-color: #a01622;
  }
`;

// Editable modal for Surat Ijin Interupsi
function SuratIjinEditModal({ selectedPermit, setSelectedPermit, adminUsers, onRefresh }) {
  const [editPermit, setEditPermit] = React.useState(selectedPermit);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Helper function to get admin name
  const getDecisionMakerName = (permit) => {
    if (!permit) {
      return 'Admin';
    }

    // Ideal case: The backend sends the related decision_by object with the name.
    if (permit.decision_by && permit.decision_by.name) {
      return permit.decision_by.name;
    }

    // Fallback: If only decision_by_id is available, look up the name in adminUsers list.
    if (permit.decision_by_id && adminUsers.length > 0) {
      // Use '==' for loose comparison to avoid issues with string vs. number IDs.
      const admin = adminUsers.find(admin => admin.id == permit.decision_by_id);
      return admin ? admin.name : 'Admin';
    }
    
    return 'Admin';
  };
  
  React.useEffect(() => {
    setEditPermit(selectedPermit);
  }, [selectedPermit]);

  // Function to validate time format (HH:MM)
  const isValidTimeFormat = (timeString) => {
    if (!timeString || timeString.trim() === '') return false;
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(timeString.trim());
  };

  // Function to update permit status
  const updatePermitStatus = async (status) => {
    if (!selectedPermit || !selectedPermit.id) return;
    
    // Validation for jam_lembur
    if (!editPermit.is_returning && !editPermit.jam_lembur) {
      setNotification({
        show: true,
        message: 'Jam Lembur wajib diisi jika siswa tidak kembali.',
        type: 'error'
      });
      return;
    }
    
    setIsSubmitting(true);
    try {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      
      // Prepare and validate data
      const requestData = {
        status,
        current_activity: editPermit.sedangApa || null,
        leave_hour: isValidTimeFormat(editPermit.pergiJam) ? editPermit.pergiJam.trim() : null,
        return_hour: isValidTimeFormat(editPermit.kembali) ? editPermit.kembali.trim() : null,
        purpose: editPermit.keperluan || null,
        destination: editPermit.tempatTujuan || null,
        pic: editPermit.penanggungjawab || null,
        relationship: editPermit.hubungan || null,
        pic_contact_number: editPermit.nomorTelepon || null,
        is_school_activity: editPermit.isSchoolActivity || false,
        is_returning: editPermit.is_returning || false,
        jam_lembur: editPermit.jam_lembur || null,
      };
      
      console.log('Sending request data:', requestData);
      
      const res = await authFetch(`${apiBase}/api/admin/interuption-permits/${selectedPermit.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(requestData)
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to update status: ${res.status} ${errorText}`);
      }
      
      const updatedPermit = await res.json();
      console.log('Status updated:', updatedPermit);
      
      // Update the local state with the new status
      const updatedLocalPermit = {
        ...editPermit,
        status: status,
        request_status: status
      };
      
      setNotification({
        show: true,
        message: status === 'disetujui' ? 'Surat ijin berhasil disetujui dan data diperbarui!' : 'Surat ijin ditolak dan data diperbarui!',
        type: status === 'disetujui' ? 'success' : 'info'
      });
      
      // Close notification after 3 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
        // Refresh the data with current filters
        if (onRefresh) {
          onRefresh();
        }
      }, 3000);
      
      // Update the permit in the parent component
      setSelectedPermit(updatedLocalPermit);
    } catch (error) {
      console.error('Error updating status:', error);
      setNotification({
        show: true,
        message: `Error: ${error.message}`,
        type: 'error'
      });
      
      // Close notification after 5 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {notification.show && (
        <NotificationToast type={notification.type}>
          {notification.message}
        </NotificationToast>
      )}
      <form
        onSubmit={e => {
          e.preventDefault();
          // Form submission is now handled by the Terima/Tolak buttons
        }}
      >
      <DetailHeader>
        <DetailHeaderContent>
          <h2>Edit Surat Ijin Interupsi</h2>
          <StatusContainer>
            <DetailStatus status={selectedPermit.status}>
              {selectedPermit.status === 'pending' || selectedPermit.status === 'menunggu' ? 'Menunggu' :
                selectedPermit.status === 'approved' || selectedPermit.status === 'disetujui' ? 'Disetujui' : 'Ditolak'}
            </DetailStatus>
            {selectedPermit.status !== 'pending' && selectedPermit.status !== 'menunggu' && (
              <DecisionMaker>
                Diputuskan oleh: {getDecisionMakerName(selectedPermit)}
              </DecisionMaker>
            )}
          </StatusContainer>
        </DetailHeaderContent>
        <CloseButton onClick={() => setSelectedPermit(null)}>×</CloseButton>
      </DetailHeader>

      <DetailContent>
        <DetailSection>
          <DetailGrid>
            <DetailGroup>
              <DetailLabel>Tanggal Pengajuan</DetailLabel>
              <input
                type="date"
                value={editPermit.tanggalPengajuanISO || ''}
                onChange={e => {
                  const newDate = e.target.value;
                  // Update both ISO and formatted date
                  setEditPermit({ 
                    ...editPermit, 
                    tanggalPengajuanISO: newDate,
                    tanggalPengajuan: newDate ? new Date(newDate).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    }) : ''
                  });
                }}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Diajukan oleh</DetailLabel>
              <DetailValue>{editPermit.diajukanOleh || ''}</DetailValue>
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Siswa yang mengajukan</DetailLabel>
              <input
                type="text"
                value={editPermit.siswaMengajukan}
                onChange={e => setEditPermit({ ...editPermit, siswaMengajukan: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Kelas</DetailLabel>
              <input
                type="text"
                value={editPermit.kelas}
                onChange={e => setEditPermit({ ...editPermit, kelas: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>NIS</DetailLabel>
              <input
                type="text"
                value={editPermit.nis}
                onChange={e => setEditPermit({ ...editPermit, nis: e.target.value })}
              />
            </DetailGroup>
          </DetailGrid>
        </DetailSection>

        <DetailSection>
          <DetailGrid>
            <DetailGroup>
              <DetailLabel>Sedang apa?</DetailLabel>
              <select
                value={editPermit.sedangApa}
                onChange={e => setEditPermit({ ...editPermit, sedangApa: e.target.value })}
              >
                <option value="Teori">Teori</option>
                <option value="Praktek">Praktek</option>
              </select>
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Pergi jam berapa</DetailLabel>
              <input
                type="time"
                value={editPermit.pergiJam}
                onChange={e => setEditPermit({ ...editPermit, pergiJam: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Kembali</DetailLabel>
              <input
                type="time"
                value={editPermit.kembali}
                onChange={e => setEditPermit({ ...editPermit, kembali: e.target.value })}
              />
            </DetailGroup>
          </DetailGrid>
        </DetailSection>

        <DetailSection>
          <DetailGroup full>
            <DetailLabel style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <input
                type="checkbox"
                checked={editPermit.isSchoolActivity || false}
                onChange={e => setEditPermit({ ...editPermit, isSchoolActivity: e.target.checked })}
                style={{ width: 'auto', margin: 0 }}
              />
              Ini adalah acara sekolah
            </DetailLabel>
            <DetailLabel style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '1rem' }}>
              <input
                type="checkbox"
                checked={editPermit.is_returning || false}
                onChange={e => setEditPermit({ ...editPermit, is_returning: e.target.checked })}
                style={{ width: 'auto', margin: 0 }}
              />
              Siswa kembali ke sekolah
            </DetailLabel>
            {!editPermit.is_returning && (
              <div style={{marginTop: '1rem'}}>
                <DetailLabel>Jam Lembur</DetailLabel>
                <input
                  type="number"
                  step="0.1"
                  value={editPermit.jam_lembur}
                  onChange={e => setEditPermit({ ...editPermit, jam_lembur: e.target.value })}
                  placeholder="Isi angka positif. Contoh: 2.5"
                  required={!editPermit.is_returning}
                />
              </div>
            )}
          </DetailGroup>
        </DetailSection>

        <DetailSection>
          <DetailGrid>
            <DetailGroup full>
              <DetailLabel>Ada keperluan apa</DetailLabel>
              <textarea
                value={editPermit.keperluan}
                onChange={e => setEditPermit({ ...editPermit, keperluan: e.target.value })}
                style={{ width: '100%', minHeight: 60 }}
              />
            </DetailGroup>
            <DetailGroup full>
              <DetailLabel>Nama tempat dan alamat tujuan</DetailLabel>
              <textarea
                value={editPermit.tempatTujuan}
                onChange={e => setEditPermit({ ...editPermit, tempatTujuan: e.target.value })}
                style={{ width: '100%', minHeight: 60 }}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Nama penanggungjawab</DetailLabel>
              <input
                type="text"
                value={editPermit.penanggungjawab}
                onChange={e => setEditPermit({ ...editPermit, penanggungjawab: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Hubungan</DetailLabel>
              <input
                type="text"
                value={editPermit.hubungan}
                onChange={e => setEditPermit({ ...editPermit, hubungan: e.target.value })}
              />
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Nomor telepon penanggungjawab</DetailLabel>
              <input
                type="text"
                value={editPermit.nomorTelepon}
                onChange={e => setEditPermit({ ...editPermit, nomorTelepon: e.target.value })}
              />
            </DetailGroup>
          </DetailGrid>
        </DetailSection>

        <ActionButtons>
        <RejectButton 
            type="button" 
            onClick={() => updatePermitStatus('ditolak')} 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Memproses...' : 'Tolak'}
          </RejectButton>
          <ApproveButton 
            type="button" 
            onClick={() => updatePermitStatus('disetujui')} 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Memproses...' : 'Disetujui'}
          </ApproveButton>
          
        </ActionButtons>
      </DetailContent>
    </form>
    </>
  );
}

const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.type === 'success' ? 'var(--success, #2ec27e)' : 'var(--danger, #c01c28)'};
  color: white;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease-out forwards;
  max-width: 350px;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const StudentLink = styled.span`
  color: var(--primary, #3b82f6);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: var(--primary-dark, #2563eb);
    text-decoration: underline;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--background-light);
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  background-color: var(--background);
  color: var(--text-dark);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const PageNumber = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  background-color: ${props => props.active ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  min-width: 40px;

  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }
`;

export default AdminSuratIjinPage;